import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CardContainer } from '../components/ui/CardContainer';
import { FormInput } from '../components/ui/FormInput';
import { PrimaryButton } from '../components/ui/PrimaryButton';
import { PASSWORD_MIN_LENGTH, PASSWORD_REGEX } from '../constants/validation';
import { env } from '../config/env';
import { CheckCircleIcon, XCircleIcon } from '@phosphor-icons/react';
import { AuthLayout } from '../components/layout/AuthLayout';

type ResetPasswordForm = {
  newPassword: string;
  confirmPassword: string;
};

export const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const token = searchParams.get('token');
  const isInvitation = searchParams.get('flow') === 'invitation';
  const flowKey = isInvitation ? 'set_password' : 'reset_password';

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordForm>();

  const newPassword = watch('newPassword');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage(t('auth.reset_password.error.missing_token'));
    }
  }, [token, t]);

  const onSubmit = async (data: ResetPasswordForm) => {
    if (!token) return;

    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch(`${env.VITE_API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword: data.newPassword,
        }),
        credentials: 'include',
      });

      if (response.ok) {
        setStatus('success');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        const result = await response.json();
        setStatus('error');

        if (result.message === 'new_password_same_as_old') {
          setErrorMessage(t('auth.reset_password.error.same_as_old'));
        } else {
          setErrorMessage(result.message || t(`auth.${flowKey}.error.invalid_token`));
        }
      }
    } catch {
      setStatus('error');
      setErrorMessage(t('auth.reset_password.error.connection_error'));
    }
  };

  if (status === 'success') {
    return (
      <AuthLayout>
        <CardContainer>
          <div className="text-center">
            <CheckCircleIcon size={64} className="mx-auto mb-4 text-green-500" weight="fill" />
            <h1 className="text-2xl font-display text-primary mb-2">
              {t(`auth.${flowKey}.success.title`)}
            </h1>
            <p className="text-secondary">{t(`auth.${flowKey}.success.description`)}</p>
          </div>
        </CardContainer>
      </AuthLayout>
    );
  }

  if (status === 'error' && !token) {
    return (
      <AuthLayout>
        <CardContainer>
          <div className="text-center">
            <XCircleIcon size={64} className="mx-auto mb-4 text-red-500" weight="fill" />
            <h1 className="text-2xl font-display text-primary mb-2">
              {t(`auth.${flowKey}.error.title`)}
            </h1>
            <p className="text-red-500 mb-4">{errorMessage}</p>
            <Link to="/forgot-password" className="text-primary hover:underline font-medium">
              {t('auth.reset_password.cta.go_to_login')}
            </Link>
          </div>
        </CardContainer>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <CardContainer>
        <h1 className="text-3xl font-display text-primary mb-2 text-center">
          {t(`auth.${flowKey}.title`)}
        </h1>
        <p className="text-secondary text-center mb-8">{t(`auth.${flowKey}.subtitle`)}</p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <FormInput
            label={t('auth.reset_password.password.label')}
            type="password"
            placeholder={t('auth.reset_password.password.placeholder')}
            error={errors.newPassword?.message}
            required
            showPasswordStrength={true}
            passwordValue={newPassword || ''}
            {...register('newPassword', {
              required: t('auth.reset_password.password.error.required'),
              minLength: {
                value: PASSWORD_MIN_LENGTH,
                message: t('auth.reset_password.password.error.min_length'),
              },
              pattern: {
                value: PASSWORD_REGEX,
                message: t('auth.reset_password.password.error.too_weak'),
              },
            })}
          />

          <FormInput
            label={t('auth.reset_password.confirm_password.label')}
            type="password"
            placeholder={t('auth.reset_password.confirm_password.placeholder')}
            error={errors.confirmPassword?.message}
            required
            {...register('confirmPassword', {
              required: t('auth.reset_password.confirm_password.error.required'),
              validate: (value) =>
                value === watch('newPassword') ||
                t('auth.reset_password.confirm_password.error.mismatch'),
            })}
          />

          {errorMessage && status === 'error' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errorMessage}</p>
            </div>
          )}

          <PrimaryButton type="submit" loading={status === 'loading'}>
            {t(`auth.${flowKey}.cta.submit`)}
          </PrimaryButton>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-primary hover:underline">
            {t('auth.reset_password.cta.go_to_login')}
          </Link>
        </div>
      </CardContainer>
    </AuthLayout>
  );
};
